// ============================================================================
// "How it works" — a reference page describing the learning model (spec §1,
// §5, §7.2, §8.1, §10). Static, theme-aware content so learners understand the
// pedagogy behind the app (the Hanja "multiplier", the two tracks, SRS, the
// recognition/production split, and the mastery states).
// ============================================================================

import { el } from '../dom.js';

function card(title, children) {
  return el('div.card.stack', {}, [el('h2', { text: title }), ...[].concat(children)]);
}

const STATES = [
  ['new', 'New', 'Not yet studied.'],
  ['learning', 'Learning', 'Seen; stepping through short intervals.'],
  ['familiar', 'Familiar', 'Retained across a few reviews (interval ≥ ~1 day).'],
  ['mastered', 'Mastered', 'Reliably recalled at long intervals (≥ 21 days).'],
  ['lapsed', 'Lapsed', 'Was known, recently missed — back to relearning.'],
];

export function renderLearn({ navigate }) {
  return el('div.container.stack', {}, [
    el('div.card.stack', {}, [
      el('h1', { html: 'How <span class="han" style="color:var(--accent)">漢字</span> learning works' }),
      el('p.muted', { text: 'Hanja are the Chinese characters behind most Korean vocabulary. Learn a character’s meaning (訓 hun) and sound (音 eum) once, and every word built from it becomes easier to acquire. That “multiplier” is the core idea of this app.' }),
    ]),

    card('Two linked tracks', [
      el('p', { html: '<strong>Hanja track</strong> — the character backbone (the MOE “Basic 1,800”): meaning, readings, radical/components, stroke order, mnemonic, etymology, and confusable look-alikes.' }),
      el('p', { html: '<strong>Word track</strong> — high-frequency Sino-Korean words (한자어), sequenced by TOPIK frequency. Every word is cross-linked to the component Hanja that build it, with the reading each contributes.' }),
      el('p.muted', { text: 'Study either way in — a character shows the words it unlocks; a word shows the characters it’s built from.' }),
    ]),

    card('A reading = hun + eum', [
      el('p', { html: 'Each character reading pairs a native-Korean meaning gloss (<strong>hun</strong>, e.g. 배울 “to learn”) with a Sino-Korean sound (<strong>eum</strong>, e.g. 학 <em>hak</em>). Example: 學 = <em>배울 학</em>. Romanization is shown throughout and can be toggled off for immersion.' }),
    ]),

    card('Two skills, tracked separately', [
      el('div.grid', {}, [
        el('div.tile', {}, [el('div.tile__value', { style: 'font-size:1.1rem', text: 'Recognition' }), el('div.tile__label', { text: 'see it → recall meaning/reading' })]),
        el('div.tile', {}, [el('div.tile__value', { style: 'font-size:1.1rem', text: 'Production' }), el('div.tile__label', { text: 'given meaning → produce it' })]),
      ]),
      el('p.muted', { text: 'Each item carries an independent schedule for each direction. An item counts as truly “mastered” only when both directions reach the 21-day interval.' }),
    ]),

    card('Spaced repetition (SM-2) & mastery states', [
      el('p', { text: 'Reviews are scheduled with an SM-2 algorithm: grade each answer Again / Hard / Good / Easy and the interval to the next review grows (or resets) accordingly. Objective games grade automatically. The queue interleaves item types and skills — it feels harder, which is exactly what makes recall durable.' }),
      el('div.stack', {}, STATES.map(([cls, name, desc]) =>
        el('div.row', {}, [el('span.badge-state', { class: `badge-state state-${cls}`, text: name }), el('span.muted', { text: desc })]))),
      el('p.faint', { text: 'Items missed repeatedly are flagged as “leeches” and given extra scaffolding (mnemonic, component breakdown, confusable contrast) instead of just re-showing.' }),
    ]),

    card('The practice games', [
      el('ul', { html: [
        '<li><strong>Flashcards</strong> — self-graded recall, the primary SRS surface.</li>',
        '<li><strong>Multiple choice</strong> — pick the meaning/reading.</li>',
        '<li><strong>Type the reading</strong> — type the eum (on-screen 두벌식 keyboard included).</li>',
        '<li><strong>Listening</strong> — hear it, pick the match.</li>',
        '<li><strong>Stroke writing</strong> — trace the character in order.</li>',
        '<li><strong>Fill the gap / Sentence order</strong> — produce words in context.</li>',
      ].join('') }),
    ]),

    el('div.card.stack', {}, [
      el('p.muted', { text: 'Ready to begin? Browse the characters to study them, then practice to lock them in.' }),
      el('div.row', {}, [
        el('button.btn.btn--primary', { text: 'Browse Hanja →', onclick: () => navigate('#/browse') }),
        el('button.btn', { text: 'Practice →', onclick: () => navigate('#/study') }),
      ]),
    ]),
  ]);
}
